/**
 * Generic DataTable — TanStack Table + shadcn Table primitives.
 * Supports: column definitions, search input (debounced), pagination,
 * loading skeleton, empty state, error state.
 * Mobile: table scrolls horizontally inside a bounded container (no page overflow).
 */
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  emptyMessage?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  pagination?: PaginationState;
  onPageChange?: (page: number) => void;
  /** Extra filter controls rendered next to search */
  filterSlot?: React.ReactNode;
}

const DEBOUNCE_MS = 300;

export function DataTable<TData>({
  columns,
  data,
  isLoading,
  isError,
  onRetry,
  emptyMessage = 'Không có dữ liệu',
  searchPlaceholder = 'Tìm kiếm...',
  searchValue = '',
  onSearchChange,
  pagination,
  onPageChange,
  filterSlot,
}: DataTableProps<TData>) {
  const [localSearch, setLocalSearch] = useState(searchValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync external changes to local
  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  function handleSearchInput(val: string) {
    setLocalSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange?.(val);
    }, DEBOUNCE_MS);
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
  });

  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.pageSize)
    : 1;

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Search + filters */}
      {(onSearchChange ?? filterSlot) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {onSearchChange && (
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-9 h-10 w-full"
                placeholder={searchPlaceholder}
                value={localSearch}
                onChange={(e) => handleSearchInput(e.target.value)}
              />
            </div>
          )}
          {filterSlot && (
            <div className="flex flex-wrap gap-2">{filterSlot}</div>
          )}
        </div>
      )}

      {/* Error state */}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-2 flex-wrap">
            <span>Không tải được dữ liệu.</span>
            {onRetry && (
              <Button size="sm" variant="outline" onClick={onRetry}>
                Thử lại
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Table — overflow-x inside bounded div, never spills page */}
      {!isError && (
        <div className="w-full overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((header) => (
                    <TableHead key={header.id} className="whitespace-nowrap">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((_, ci) => (
                      <TableCell key={ci}>
                        <Skeleton className="h-4 w-full rounded" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="hover:bg-accent transition-colors"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">
            Trang {pagination.page} / {totalPages} &nbsp;(
            {pagination.total} bản ghi)
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange?.(pagination.page - 1)}
            >
              ‹
            </Button>
            {(() => {
              // Sliding window centered on current page, clamped within [1, totalPages].
              const visibleCount = Math.min(5, totalPages);
              const startPage = Math.max(
                1,
                Math.min(pagination.page - 2, totalPages - visibleCount + 1),
              );
              return Array.from({ length: visibleCount }, (_, i) => {
                const p = startPage + i;
                return (
                  <Button
                    key={p}
                    variant={p === pagination.page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onPageChange?.(p)}
                  >
                    {p}
                  </Button>
                );
              });
            })()}
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= totalPages}
              onClick={() => onPageChange?.(pagination.page + 1)}
            >
              ›
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
